import { type ReactElement } from 'react'
import { useRouter } from 'next/router'
import { Box, styled, Typography, Button, Grid, ButtonBase } from '@mui/material'
// import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import CardContainer from '@/components/dashboard/CardContainer'
import SafeAppIconCard from '@/components/safe-apps/SafeAppIconCard'
import { INHERIT_EVENTS, trackEvent } from '@/services/analytics'
import { AppRoutes } from '@/config/routes'

const Btn = styled(Button)(`
    height: 32px;
    padding: 10px 16px;
    font-size: 14px;
    font-style: normal;
    font-weight: bold;
    line-height: 160%;
    border-radius: 4px;
    border: 1px solid var(--Emerald-600, #22C55E);
`)

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <Box
      flex={1}
      alignItems="center"
      justifyContent="center"
      p={0}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </Box>
  )
}

const Apps = ({ showHeader = true }: { showHeader?: boolean }): ReactElement => {
  return (
    <CardContainer
      title={showHeader ? 'Applications' : ''}
      // headerBtn={<Btn startIcon={<ArrowRightIcon sx={{ fontSize: '24px !important' }} />}>Explore more apps</Btn>}
    >
      <Grid container px={showHeader ? '16px' : '0px'} spacing={2}>
        {apps.map((app, index) => (
          <AppCard key={`app-${index}`} app={app} />
        ))}
      </Grid>
    </CardContainer>
  )
}

type AppInfo = {
  name: string
  description: string
  tags: string[]
  icon: string
  url?: string
}

const apps: AppInfo[] = [
  // {
  //   name: 'Quick Swap',
  //   description: 'Unlock the world of cryptocurrency trading.',
  //   tags: ['DeFi', 'DEX'],
  //   icon: 'https://assets.coingecko.com/coins/images/12271/standard/512x512_Logo_no_chop.png?1696512101',
  //   url: 'https://traderjoexyz.com/avalanche',
  // },
  {
    name: 'Uniswap',
    description: 'Swap or provide liquidity on the Uniswap Protocol',
    tags: ['DeFi', 'DEX'],
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/7083.png',
    url: 'https://app.uniswap.org/',
  },
  {
    name: 'Aave',
    description: 'Lend, borrow, and earn interest on the Aave Protocol',
    tags: ['DeFi', 'Lend', 'Borrow'],
    icon: 'https://safe-transaction-assets.safe.global/safe_apps/18/icon.png',
    url: 'https://app.aave.com/',
  },
  // {
  //   name: 'PancakeSwap',
  //   description: 'Trade, earn, and own crypto on the all-in-one multichain DEX',
  //   tags: ['DeFi', 'DEX'],
  //   icon: 'https://s3.coinmarketcap.com/static/img/portraits/622adc153bfcf86d42d1bc76.png',
  //   url: 'https://embedded.paraswap.io/',
  // },
]

const AppCard = ({ app }: { app: AppInfo }): ReactElement => {
  const router = useRouter()

  const handleClickApp = () => {
    trackEvent({ ...INHERIT_EVENTS.CLICK_APPS, ...{ label: app.name } })

    if (app.url) {
      router.push({ pathname: AppRoutes.apps.open, query: { ...router.query, appUrl: app.url } })
    }
  }
  return (
    <Grid item xs={12} sm={6}>
      <ButtonBase
        sx={{
          textAlign: 'left',
          width: '100%',
          borderRadius: '6px',
          border: '1px solid var(--Ebony-Clay-950, #2F354B)',
          // background: 'var(--Supporting-Dark-BG-700, #272A3E)',
          '&:hover': { background: 'var(--Ebony-Clay-900, #343c58)' },
        }}
        onClick={handleClickApp}
      >
        <Grid
          container
          sx={{
            padding: '16px',
          }}
        >
          <Grid item container xs={12} justifyContent="space-between">
            <Grid item xs="auto">
              <SafeAppIconCard src={app.icon} alt={app.name} width={48} height={48} />
            </Grid>
            <Grid item xs="auto">
              {!app.url && <CommingSoon />}
              {app.url && <New />}
            </Grid>
          </Grid>
          <Grid item xs={12} mt="4px">
            <Typography
              sx={{
                color: 'var(--Ebony-Clay-300)',
                fontSize: '16px',
                fontWeight: 'bold',
                lineHeight: '160%',
                marginBottom: '4px',
              }}
              variant="body1"
              component="h2"
            >
              {app.name}
            </Typography>
            <Typography
              sx={{
                color: 'var(--Ebony-Clay-400)',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '160%',
              }}
              variant="body1"
              component="h2"
            >
              {app.description}
            </Typography>
          </Grid>
          <Grid item container xs={12} mt="12px">
            {app.tags.map((tag, index) => (
              <Tag key={`tag-${index}`} name={tag} />
            ))}
          </Grid>
        </Grid>
      </ButtonBase>
    </Grid>
  )
}

const Tag = ({ name }: { name: string }): ReactElement => {
  return (
    <Typography
      sx={{
        color: 'var(--Ebony-Clay-300)',
        fontSize: '12px',
        fontWeight: 'bold',
        lineHeight: '160%',
        // border: '1px solid var(--Ebony-Clay-600)',
        paddingX: '8px',
        paddingY: '4px',
        width: 'fit-content',
        borderRadius: '4px',
        background: 'var(--Ebony-Clay-950, #2F354B)',
        marginRight: '8px',
        marginTop: '8px',
      }}
      variant="body1"
      component="h2"
    >
      {name}
    </Typography>
  )
}

const CommingSoon = (): ReactElement => {
  return (
    <Typography
      sx={{
        background: 'var(--Ebony-Clay-600)',
        color: 'var(--Ebony-Clay-300)',
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '140%',
        border: '1px solid var(--Ebony-Clay-600)',
        borderRadius: '4px',
        paddingX: '8px',
      }}
      variant="body1"
      component="h2"
    >
      Comming soon
    </Typography>
  )
}

const New = (): ReactElement => {
  return (
    <Typography
      sx={{
        // background: 'var(--Emerald-600, #22C55E)',
        color: 'var(--Emerald-600, #22C55E)',
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '140%',
        border: '1px solid var(--Emerald-600, #22C55E)',
        borderRadius: '4px',
        paddingX: '8px',
      }}
      variant="body1"
      component="h2"
    >
      New
    </Typography>
  )
}

export default Apps
